import { useEffect, useState, useRef } from "react";
import { motion, useInView, useSpring, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  delay?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  once?: boolean;
}

/**
 * AnimatedCounter - Number roll-up animation for statistics
 * Uses spring physics for satisfying deceleration
 */
export const AnimatedCounter = ({
  value,
  duration = 1.5,
  delay = 0,
  prefix = "",
  suffix = "",
  className,
  once = true,
}: AnimatedCounterProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { margin: "0px" });

  const springValue = useSpring(0, {
    duration: duration * 1000,
    bounce: 0,
  });

  const displayValue = useTransform(springValue, (latest) =>
    Math.round(latest)
  );

  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    const unsubscribe = displayValue.on("change", (v) => {
      setCurrentValue(v);
    });
    return unsubscribe;
  }, [displayValue]);

  useEffect(() => {
    if (isInView && value > 0) {
      const timeout = setTimeout(() => {
        springValue.set(value);
      }, delay * 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, value, springValue, delay]);

  return (
    <motion.span
      ref={ref}
      className={cn("tabular-nums", className)}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4, ease: "easeOut", delay }}
    >
      {prefix}
      {currentValue.toLocaleString()}
      {suffix}
    </motion.span>
  );
};
